import type { Character } from '../domain/character.ts';
import { STAT_LABELS } from '../domain/stats.ts';
import type { CheckHistoryEntry, DamageHistoryEntry } from '../rules/rollHistory.ts';
import type { AttackCheckOutcome } from '../rules/attackResolution.ts';
import type { DamageRollResult } from '../rules/damageRoll.ts';
import type { AdvancementRoll } from '../rules/skillAdvancement.ts';
import type { AttackPlan } from './attackService.ts';
import type { EffectiveCharacter } from './effectiveCharacter.ts';
import type { CheckContext } from '../rules/effects.ts';
import type { RollMode } from '../rules/rollModes.ts';

/**
 * Freezes attack, damage and advancement rolls into history entries.
 *
 * Every value is copied as a NUMBER at the moment of the roll. Nothing here
 * holds a reference to the character, so a later Rank or Stat change can never
 * rewrite what a past roll showed.
 */

export function attackHistoryEntry(args: {
  id: string;
  timestamp: string;
  plan: AttackPlan;
  outcome: AttackCheckOutcome;
  effective?: EffectiveCharacter;
  manualMode?: RollMode;
}): CheckHistoryEntry {
  const { plan, outcome } = args;
  const check = outcome.check;
  const rankComponent = check.components.find((c) => c.label.endsWith('Rank'));
  const context: CheckContext = {
    kind: 'attack',
    stat: plan.toHitStat,
    skillId: plan.skill.id,
  };

  return {
    id: args.id,
    timestamp: args.timestamp,
    rollType: 'attack',
    label: `Attack: ${plan.skill.name}`,
    statId: plan.toHitStat,
    statAbbr: STAT_LABELS[plan.toHitStat].abbr,
    statMod: plan.toHitMod,
    skillId: plan.skill.id,
    skillName: plan.skill.name,
    skillRank: rankComponent ? rankComponent.value : 0,
    mode: check.die.mode,
    manualMode: args.manualMode ?? check.die.mode,
    effectSources: args.effective ? args.effective.effectSourcesFor(context) : [],
    dice: [...check.die.dice],
    keptDie: check.die.kept,
    extraComponents: check.components
      .filter((c) => !c.label.endsWith('Rank') && !c.label.endsWith('Mod'))
      .map((c) => ({ ...c })),
    total: check.total,
    untrained: check.untrained,
    // For an Attack Check the Difficulty IS the target's Evade.
    difficulty: outcome.resolution ? outcome.resolution.difficulty : null,
    degree: outcome.resolution ? outcome.resolution.degree : null,
  };
}

export function damageHistoryEntry(args: {
  id: string;
  timestamp: string;
  plan: AttackPlan;
  damage: DamageRollResult;
  effective?: EffectiveCharacter;
}): DamageHistoryEntry {
  const context: CheckContext = {
    kind: 'attack',
    stat: args.plan.profile.baseDamage.stat,
    skillId: args.plan.skill.id,
  };
  return {
    id: args.id,
    timestamp: args.timestamp,
    rollType: 'damage',
    label: `Damage: ${args.plan.skill.name}`,
    skillId: args.plan.skill.id,
    skillName: args.plan.skill.name,
    damageType: args.damage.damageType,
    components: args.damage.components.map((c) => ({ ...c })),
    total: args.damage.total,
    criticalHit: args.damage.criticalHit,
    effectSources: args.effective
      ? args.effective.effectSourcesFor(context).filter((s) => s.kind === 'modifier')
      : [],
  };
}

/**
 * A Debuff's end-of-round damage, frozen into the same DamageHistoryEntry shape
 * an attack uses. There is no separate encounter damage history.
 */
export function recurringDamageHistoryEntry(args: {
  id: string;
  timestamp: string;
  label: string;
  sourceId: string;
  damage: DamageRollResult;
}): DamageHistoryEntry {
  return {
    id: args.id,
    timestamp: args.timestamp,
    rollType: 'damage',
    label: `${args.label} damage`,
    skillId: null,
    skillName: args.label,
    damageType: args.damage.damageType,
    components: args.damage.components.map((c) => ({ ...c })),
    total: args.damage.total,
    criticalHit: args.damage.criticalHit,
    effectSources: [
      {
        kind: 'modifier',
        label: args.label,
        sourceId: args.sourceId,
        target: 'End-of-round damage',
        amount: args.damage.total,
      },
    ],
  };
}

export function advancementHistoryEntry(args: {
  id: string;
  timestamp: string;
  roll: AdvancementRoll;
  skillName: string;
}): CheckHistoryEntry {
  const { roll } = args;
  return {
    id: args.id,
    timestamp: args.timestamp,
    rollType: 'advancement',
    label: `Advancement: ${args.skillName}`,
    statId: null,
    statAbbr: null,
    statMod: null,
    skillId: roll.skillId,
    skillName: args.skillName,
    // The Rank at the time of the roll, which was also the Difficulty.
    skillRank: roll.rankBefore,
    mode: 'normal',
    manualMode: 'normal',
    effectSources: [],
    dice: [roll.die],
    keptDie: roll.die,
    extraComponents: [],
    total: roll.die,
    untrained: false,
    difficulty: roll.rankBefore,
    degree: roll.success ? 'advancement-success' : 'advancement-fail',
  };
}

/** Convenience for the Play Screen: the character is only read, never stored. */
export function describeCharacterForHistory(character: Character): string {
  return character.name;
}
